import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import { usePortalChat } from "./context";
import { VirtualizedList } from "./VirtualizedList";
import { useChatDirectory } from "../hooks/useChatDirectory";
import {
  PICKER_ROW_HEIGHT,
  ATTACHMENT_ROW_HEIGHT,
  GROUP_NAME_MAX_LENGTH,
  TOPIC_MAX_LENGTH,
  SEARCH_MAX_LENGTH,
} from "../portalChat.constants";
import {
  addMembersRequest,
  removeMemberRequest,
  renameConversationRequest,
  getConversationAttachmentsRequest,
  type ChatConversation,
  type ChatDirectoryUser,
  type ChatAttachmentItem,
} from "../api/portalChat.api";

/** Members list for group/space chats; add (any member) and remove (effective owner) for groups. */
export function MembersDialog({
  conversation,
  onClose,
}: {
  readonly conversation: ChatConversation;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const threadMembers = chat.thread?.members;
  const members = useMemo(() => threadMembers ?? [], [threadMembers]);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ChatDirectoryUser[]>([]);
  const [busy, setBusy] = useState(false);

  const isGroup = conversation.type === "group";
  const isOwner = String(conversation.createdBy) === chat.userId;

  // Server-side search + pagination, fetched only while the add flow is open.
  const { users: directory, loadMore } = useChatDirectory(search, adding);
  const candidates = useMemo(() => {
    const memberIds = new Set(members.map((member) => String(member._id)));
    return directory.filter((user) => !memberIds.has(String(user._id)));
  }, [directory, members]);

  const submitAdd = async () => {
    if (!selected.length || busy) return;
    setBusy(true);
    try {
      const response = await addMembersRequest({
        conversationId: toObjectIdString(conversation._id),
        memberIds: selected.map((user) => toObjectIdString(String(user._id))),
      });
      if (response.data.status) {
        chat.showToast(t("Members added"));
        setAdding(false);
        setSelected([]);
        await chat.refreshList();
      } else {
        chat.showServerError(response.data.errorMessage, t("Could not add members"));
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await removeMemberRequest({
        conversationId: toObjectIdString(conversation._id),
        userId: toObjectIdString(userId),
      });
      if (response.data.status) {
        chat.showToast(t("Member removed"));
        await chat.refreshList();
      } else {
        chat.showServerError(response.data.errorMessage, t("Could not remove the member"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="28rem"
      headerTitle={
        <span>
          {t("Members")} ({members.length})
        </span>
      }
      onCancel={onClose}
      footerContent={<></>}
      modelContent={
        <div className="flex max-h-[60vh] w-full flex-col gap-2">
          {conversation.type === "space" && (
            <div className="rounded-xs bg-[#f6fafd] px-2 py-1 text-xs text-gray-500">
              {t("Membership follows the Space — manage members from the Space settings.")}
            </div>
          )}
          {isGroup && !adding && (
            <ButtonComponent buttonType="borderButton" onClick={() => setAdding(true)}>
              {t("Add members")}
            </ButtonComponent>
          )}
          {isGroup && adding && (
            <div className="flex flex-col gap-2 rounded-xs border border-[#e3ecf5] p-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxLength={SEARCH_MAX_LENGTH}
                placeholder={t("Search people")}
                className="w-full rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
              />
              <div className="h-40">
                {candidates.length > 0 && (
                  <VirtualizedList
                    items={candidates}
                    rowHeight={PICKER_ROW_HEIGHT}
                    onEndReached={loadMore}
                    ariaLabel={t("Search people")}
                    renderRow={(user) => {
                      const isSelected = selected.some((u) => String(u._id) === String(user._id));
                      return (
                        <button
                          type="button"
                          onClick={() =>
                            setSelected((prev) =>
                              isSelected ? prev.filter((u) => String(u._id) !== String(user._id)) : [...prev, user],
                            )
                          }
                          className={`flex h-full w-full items-center gap-2 px-1 text-start text-sm hover:bg-[#f0f5f9] ${
                            isSelected ? "bg-[#e8f1fc]" : ""
                          }`}
                        >
                          <UserNameTagComponent showAvatarOnly id={String(user._id)} size="XS" isCircle />
                          <span className="truncate">{user.fullName}</span>
                        </button>
                      );
                    }}
                  />
                )}
                {!candidates.length && (
                  <div className="p-2 text-xs text-gray-400">{t("No members match your search.")}</div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <ButtonComponent
                  buttonType="borderButton"
                  onClick={() => {
                    setAdding(false);
                    setSelected([]);
                  }}
                >
                  {t("Cancel")}
                </ButtonComponent>
                <ButtonComponent
                  buttonType="gradientButton"
                  disabled={!selected.length || busy}
                  onClick={() => void submitAdd()}
                >
                  {t("Add")}
                </ButtonComponent>
              </div>
            </div>
          )}
          <div className="h-[40vh]">
            <VirtualizedList
              items={members}
              rowHeight={PICKER_ROW_HEIGHT}
              ariaLabel={t("Members")}
              renderRow={(member) => (
                <div className="flex h-full items-center justify-between px-1">
                  <span className="flex items-center gap-2 text-sm">
                    <UserNameTagComponent showAvatarOnly id={String(member._id)} size="XS" isCircle />
                    <span>
                      {String(member._id) === chat.userId ? `${member.fullName} (${t("You")})` : member.fullName}
                      {isGroup && String(member._id) === String(conversation.createdBy) && (
                        <span className="ms-1 rounded-xs bg-[#e8f1fc] px-1 text-[0.6rem] text-[#1E88E5]">
                          {t("Owner")}
                        </span>
                      )}
                    </span>
                  </span>
                  {isGroup && isOwner && String(member._id) !== chat.userId && (
                    <IconComponent
                      name="DeleteIcon"
                      width="0.9rem"
                      height="0.9rem"
                      toolTipContent={t("Remove")}
                      onClick={() => void remove(String(member._id))}
                    />
                  )}
                </div>
              )}
            />
          </div>
        </div>
      }
    />
  );
}

/** View-attachments gallery: search by name, newest/oldest sort, multi-select download (PRD). */
export function AttachmentsDialog({
  conversation,
  onClose,
}: {
  readonly conversation: ChatConversation;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<ChatAttachmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newestFirst, setNewestFirst] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    // Fire-and-forget: an effect callback cannot be async; the chain guards on `cancelled` and
    // clears its own loader.
    void getConversationAttachmentsRequest({ conversationId: toObjectIdString(conversation._id) })
      .then((response) => {
        if (!cancelled && response.data.status) setItems(response.data.result ?? []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conversation._id]);

  const visible = useMemo(() => {
    const lower = search.trim().toLowerCase();
    const filtered = items.filter((item) => !lower || item.attachmentsData.name.toLowerCase().includes(lower));
    return newestFirst ? filtered : [...filtered].reverse();
  }, [items, search, newestFirst]);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const downloadSelected = () => {
    for (const item of visible) {
      if (selected.has(item.awsKey) && item.downloadLink) window.open(item.downloadLink, "_blank");
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="30rem"
      headerTitle={t("Attachments")}
      onCancel={onClose}
      modelContent={
        <div className="flex max-h-[60vh] w-full flex-col gap-2">
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxLength={SEARCH_MAX_LENGTH}
              placeholder={t("Search attachments")}
              className="min-w-0 flex-1 rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
            />
            <button
              type="button"
              className="rounded-xs border border-[#dce6f1] px-2 py-1 text-xs"
              onClick={() => setNewestFirst((prev) => !prev)}
            >
              {newestFirst ? t("Newest first") : t("Oldest first")}
            </button>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={visible.length > 0 && visible.every((item) => selected.has(item.awsKey))}
              onChange={(e) => setSelected(e.target.checked ? new Set(visible.map((item) => item.awsKey)) : new Set())}
            />
            {t("Select all")}
          </label>
          <div className="h-[40vh]">
            {loading && <div className="p-3 text-sm text-gray-400">{t("Loading")}…</div>}
            {!loading && !items.length && (
              <div className="flex flex-col items-center gap-1 p-4 text-center">
                <span className="text-sm font-semibold text-gray-500">{t("No attachments yet")}</span>
                <span className="text-xs text-gray-400">
                  {t("Files and photos shared in this conversation will appear here.")}
                </span>
              </div>
            )}
            {!loading && items.length > 0 && !visible.length && (
              <div className="p-3 text-sm text-gray-400">{t("No attachments match your search.")}</div>
            )}
            {visible.length > 0 && (
              <VirtualizedList
                items={visible}
                rowHeight={ATTACHMENT_ROW_HEIGHT}
                ariaLabel={t("Attachments")}
                renderRow={(item) => (
                  <div className="flex h-full items-center gap-2 px-1">
                    <input type="checkbox" checked={selected.has(item.awsKey)} onChange={() => toggle(item.awsKey)} />
                    <IconComponent
                      name={item.attachmentsData.type?.startsWith("image/") ? "image" : "attachFile"}
                      width="0.9rem"
                      height="0.9rem"
                    />
                    <a
                      href={item.downloadLink}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 flex-1 truncate text-sm text-[#1E88E5] hover:underline"
                    >
                      {item.attachmentsData.name}
                    </a>
                    <span className="whitespace-nowrap text-xs text-gray-400">
                      {item.senderName} · {new Date(item.createdDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              />
            )}
          </div>
        </div>
      }
      footerContent={
        <div className="flex w-full items-center justify-between">
          <span className="text-xs text-gray-400">
            {selected.size
              ? t("{{selected}} of {{total}} selected", { selected: selected.size, total: visible.length })
              : t("{{total}} attachments", { total: visible.length })}
          </span>
          <ButtonComponent buttonType="gradientButton" disabled={!selected.size} onClick={downloadSelected}>
            {t("Download")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/** Rename a group chat: name required, topic optional (PRD). */
export function RenameDialog({
  conversation,
  onClose,
}: {
  readonly conversation: ChatConversation;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [name, setName] = useState(conversation.name);
  const [topic, setTopic] = useState(conversation.topic ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const response = await renameConversationRequest({
        conversationId: toObjectIdString(conversation._id),
        name: name.trim(),
        topic: topic.trim(),
      });
      if (response.data.status) {
        chat.showToast(t("Conversation renamed."));
        await chat.refreshList();
        onClose();
      } else {
        chat.showServerError(response.data.errorMessage, t("Could not rename the chat"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="24rem"
      headerTitle={t("Name this conversation")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-2">
          <div className="text-xs text-gray-500">{t("Everyone in the group sees the new name.")}</div>
          <label className="text-xs text-gray-500">{t("Group name")}</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={GROUP_NAME_MAX_LENGTH}
            className="w-full rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
          />
          <label className="text-xs text-gray-500">{t("Topic (optional)")}</label>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            maxLength={TOPIC_MAX_LENGTH}
            className="w-full rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
          />
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-2">
          <ButtonComponent buttonType="borderButton" onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent buttonType="gradientButton" disabled={!name.trim() || busy} onClick={() => void save()}>
            {t("Save")}
          </ButtonComponent>
        </div>
      }
    />
  );
}
